/**
 * Formatting for an Indian audience. One module, so "14 Mar 2026" is the same
 * string on the home screen, the admin table and the review summary.
 */

const LOCALE = 'en-IN'

const dateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

const longDateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const dateTimeFormat = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const timeFormat = new Intl.DateTimeFormat(LOCALE, {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const numberFormat = new Intl.NumberFormat(LOCALE)

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** `29 Jul 2026` */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value)
  return date ? dateFormat.format(date) : '—'
}

/** `29 July 2026` — used in the document and on confirmation screens. */
export function formatLongDate(value: string | Date | null | undefined): string {
  const date = toDate(value)
  return date ? longDateFormat.format(date) : '—'
}

/** `29 Jul 2026, 4:12 pm` */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value)
  return date ? dateTimeFormat.format(date) : '—'
}

/** `4:12 pm` — the app bar autosave indicator. */
export function formatTime(value: string | Date | null | undefined): string {
  const date = toDate(value)
  return date ? timeFormat.format(date) : ''
}

export function formatNumber(value: number): string {
  return numberFormat.format(value)
}

/**
 * Whole days since `value`. Used for the admin AGE column, which turns
 * warning at 3 days and danger at 7 — always with the word "overdue" beside
 * it, never colour alone.
 */
export function daysSince(value: string | Date | null | undefined, now = new Date()): number {
  const date = toDate(value)
  if (!date) return 0
  return Math.floor((now.getTime() - date.getTime()) / 86_400_000)
}

/** `2d`, `6h`, `18m` — compact age for a dense table cell. */
export function formatAge(value: string | Date | null | undefined, now = new Date()): string {
  const date = toDate(value)
  if (!date) return '—'
  const minutes = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60_000))
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

/** `in 2 days` / `2 days ago` — the notary response deadline. */
export function formatRelativeDeadline(
  value: string | Date | null | undefined,
  now = new Date(),
): string {
  const date = toDate(value)
  if (!date) return '—'
  const hours = Math.round((date.getTime() - now.getTime()) / 3_600_000)
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour')
  return rtf.format(Math.round(hours / 24), 'day')
}

// --- phone ------------------------------------------------------------------

/** `+919876543210` → `+91 98765 43210`. Anything unexpected passes through. */
export function formatMobile(e164: string | null | undefined): string {
  if (!e164) return '—'
  const digits = e164.replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`
  }
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`
  return e164
}

/** `+919876543210` → `9876543210`, for editing in the national-format field. */
export function mobileNationalPart(e164: string | null | undefined): string {
  if (!e164) return ''
  const digits = e164.replace(/\D/g, '')
  return digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits.slice(-10)
}

export function toE164Mobile(national: string): string {
  const digits = national.replace(/\D/g, '').slice(-10)
  return digits ? `+91${digits}` : ''
}

// --- addresses --------------------------------------------------------------

export function formatAddress(address: {
  line1?: string | null
  line2?: string | null
  city?: string | null
  state?: string | null
  pincode?: string | null
}): string {
  const tail = [address.state, address.pincode].filter(Boolean).join(' ')
  return [address.line1, address.line2, address.city, tail].filter(Boolean).join(', ')
}

// --- dates as three boxes ---------------------------------------------------

export interface DateParts {
  day: string
  month: string
  year: string
}

export function splitDate(iso: string | null | undefined): DateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '')
  if (!match) return { day: '', month: '', year: '' }
  return { day: match[3] ?? '', month: match[2] ?? '', year: match[1] ?? '' }
}

/** Returns an ISO date, or `''` when the three boxes are not a real date. */
export function joinDate(parts: DateParts): string {
  const day = Number(parts.day)
  const month = Number(parts.month)
  const year = Number(parts.year)
  if (!day || !month || !year || parts.year.length !== 4) return ''
  if (month < 1 || month > 12 || day < 1 || day > 31) return ''
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return ''
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function ageInYears(iso: string, now = new Date()): number {
  const birth = new Date(`${iso}T00:00:00Z`)
  let age = now.getUTCFullYear() - birth.getUTCFullYear()
  const monthDelta = now.getUTCMonth() - birth.getUTCMonth()
  if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < birth.getUTCDate())) age -= 1
  return age
}
